/**
 * PortalPixels — Tracking pixel / conversion API manager
 *
 * Affiliates paste their Facebook/Google/TikTok pixel IDs.
 * IntentDrive fires conversion events automatically on conversion.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Scan, Plus, Loader2, Trash2, Pencil, Save, ArrowLeft, ToggleLeft, ToggleRight } from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch, supabaseDelete } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';

interface TrackingPixel {
  id: string;
  name: string;
  platform: string;
  pixel_id: string;
  access_token: string | null;
  fire_on: string;
  event_name: string;
  send_value: boolean;
  send_email: boolean;
  send_phone: boolean;
  custom_params: Record<string, unknown>;
  is_active: boolean;
  total_fired: number;
  last_fired_at: string | null;
  created_at: string;
}

const PLATFORMS = [
  { value: 'facebook', label: 'Facebook / Meta', color: 'text-blue-400', icon: 'f' },
  { value: 'google', label: 'Google Ads', color: 'text-red-400', icon: 'G' },
  { value: 'tiktok', label: 'TikTok', color: 'text-pink-400', icon: 'T' },
  { value: 'snapchat', label: 'Snapchat', color: 'text-yellow-400', icon: 'S' },
  { value: 'twitter', label: 'Twitter / X', color: 'text-slate-300', icon: 'X' },
  { value: 'pinterest', label: 'Pinterest', color: 'text-red-500', icon: 'P' },
  { value: 'taboola', label: 'Taboola', color: 'text-blue-300', icon: 'Tb' },
  { value: 'outbrain', label: 'Outbrain', color: 'text-orange-400', icon: 'Ob' },
  { value: 'custom', label: 'Custom Pixel', color: 'text-slate-400', icon: '?' },
];

const FIRE_ON = [
  { value: 'conversion', label: 'Conversion (lead submitted / call connected)' },
  { value: 'lead', label: 'Lead Capture (email collected)' },
  { value: 'page_view', label: 'Page View' },
  { value: 'call', label: 'Call Connected' },
  { value: 'all', label: 'All Events' },
];

export default function PortalPixels() {
  const { user } = useAuth();
  const [pixels, setPixels] = useState<TrackingPixel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<TrackingPixel>>({});
  const [saving, setSaving] = useState(false);

  const [formPlatform, setFormPlatform] = useState('facebook');
  const [formPixelId, setFormPixelId] = useState('');
  const [formName, setFormName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setPixels((await supabaseGet('tracking_pixels?order=created_at.desc')) || []); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!formPixelId || !user?.id) return;
    setCreating(true);
    try {
      await supabasePost('tracking_pixels', { user_id: user.id, platform: formPlatform, pixel_id: formPixelId, name: formName || `${formPlatform} pixel`, fire_on: 'conversion', event_name: formPlatform === 'facebook' ? 'Purchase' : 'conversion', is_active: true });
      setShowCreate(false); setFormPixelId(''); setFormName(''); load();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const startEdit = (p: TrackingPixel) => { setEditingId(p.id); setEditForm({ ...p }); };
  const saveEdit = async () => {
    if (!editingId) return; setSaving(true);
    try {
      const { id, total_fired, last_fired_at, created_at, ...updates } = editForm as any;
      await supabasePatch(`tracking_pixels?id=eq.${editingId}`, updates);
      setPixels(prev => prev.map(p => p.id === editingId ? { ...p, ...updates } : p));
      setEditingId(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const toggle = async (p: TrackingPixel) => {
    await supabasePatch(`tracking_pixels?id=eq.${p.id}`, { is_active: !p.is_active }).catch(() => {});
    setPixels(prev => prev.map(x => x.id === p.id ? { ...x, is_active: !x.is_active } : x));
  };

  const u = (k: string, v: any) => setEditForm(f => ({ ...f, [k]: v }));

  if (editingId) {
    const plat = PLATFORMS.find(p => p.value === editForm.platform);
    return (
      <PortalLayout>
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
            <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save</Button>
          </div>
          <h1 className="text-xl font-bold text-white">{editForm.name}</h1>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400 block mb-1">Name</label><input value={editForm.name || ''} onChange={e => u('name', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                <div><label className="text-xs text-slate-400 block mb-1">Platform</label><select value={editForm.platform} onChange={e => u('platform', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">{PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</select></div>
                <div><label className="text-xs text-slate-400 block mb-1">Pixel ID</label><input value={editForm.pixel_id || ''} onChange={e => u('pixel_id', e.target.value)} placeholder={editForm.platform === 'facebook' ? '1234567890' : editForm.platform === 'google' ? 'AW-1234567890' : 'Pixel ID'} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono" /></div>
                <div><label className="text-xs text-slate-400 block mb-1">Access Token (for server-side API)</label><input value={editForm.access_token || ''} onChange={e => u('access_token', e.target.value)} type="password" placeholder="Optional" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400 block mb-1">Fire On</label><select value={editForm.fire_on} onChange={e => u('fire_on', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">{FIRE_ON.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}</select></div>
                <div><label className="text-xs text-slate-400 block mb-1">Event Name</label><input value={editForm.event_name || ''} onChange={e => u('event_name', e.target.value)} placeholder="Purchase" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={editForm.send_value || false} onChange={e => u('send_value', e.target.checked)} className="rounded border-slate-600" /> Send conversion value (payout amount)</label>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={editForm.send_email || false} onChange={e => u('send_email', e.target.checked)} className="rounded border-slate-600" /> Send email (for advanced matching)</label>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={editForm.send_phone || false} onChange={e => u('send_phone', e.target.checked)} className="rounded border-slate-600" /> Send phone (for advanced matching)</label>
              </div>
              {/* Platform-specific help */}
              {editForm.platform === 'facebook' && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-400">
                  <strong>Facebook:</strong> Pixel ID from Events Manager. Access Token from Business Settings → System Users. Event name defaults to "Purchase" — change to "Lead" for lead gen.
                </div>
              )}
              {editForm.platform === 'google' && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
                  <strong>Google Ads:</strong> Format: AW-XXXXXXXXXX/XXXXXXXXXXX. Get from Google Ads → Tools → Conversions.
                </div>
              )}
              {editForm.platform === 'tiktok' && (
                <div className="p-3 bg-pink-500/10 border border-pink-500/30 rounded-lg text-xs text-pink-400">
                  <strong>TikTok:</strong> Pixel ID from TikTok Ads Manager → Events. Access Token for Events API from Developer Portal.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Scan className="h-6 w-6 text-emerald-400" /> Tracking Pixels</h1>
            <p className="text-sm text-slate-400 mt-1">Paste your ad platform pixel IDs. IntentDrive fires conversion events automatically.</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4 mr-1" /> Add Pixel</Button>
        </div>

        {showCreate && (
          <Card className="bg-slate-800/50 border-emerald-500/30">
            <CardContent className="p-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-slate-400 block mb-1">Platform</label><select value={formPlatform} onChange={e => setFormPlatform(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">{PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</select></div>
                <div><label className="text-xs text-slate-400 block mb-1">Pixel ID *</label><input value={formPixelId} onChange={e => setFormPixelId(e.target.value)} placeholder="1234567890" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono" /></div>
                <div><label className="text-xs text-slate-400 block mb-1">Name</label><input value={formName} onChange={e => setFormName(e.target.value)} placeholder="My FB Pixel" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              </div>
              <Button onClick={create} disabled={creating || !formPixelId} className="bg-emerald-600 hover:bg-emerald-700">{creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Add Pixel</Button>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : pixels.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-8 text-center"><Scan className="h-10 w-10 text-slate-600 mx-auto mb-3" /><p className="text-sm text-slate-400">No tracking pixels configured.</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {pixels.map(p => {
              const plat = PLATFORMS.find(x => x.value === p.platform);
              return (
                <Card key={p.id} className={`border ${p.is_active ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-60'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <button onClick={() => toggle(p)} className="shrink-0">{p.is_active ? <ToggleRight className="h-6 w-6 text-emerald-400" /> : <ToggleLeft className="h-6 w-6 text-slate-600" />}</button>
                      <div className={`w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold ${plat?.color || 'text-slate-400'}`}>{plat?.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{p.name}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-600/20 text-slate-400">{plat?.label}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-500/20 text-violet-400">{p.fire_on}</span>
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5">{p.pixel_id}</div>
                        <div className="text-[10px] text-slate-600 mt-0.5">{p.total_fired} fired {p.last_fired_at && `• Last: ${new Date(p.last_fired_at).toLocaleString()}`}</div>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <button onClick={() => startEdit(p)} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600/20 text-xs text-emerald-400 hover:bg-emerald-600/30"><Pencil className="h-3 w-3" /> Edit</button>
                        <button onClick={async () => { await supabaseDelete(`tracking_pixels?id=eq.${p.id}`).catch(() => {}); setPixels(prev => prev.filter(x => x.id !== p.id)); }} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
